import { Injectable } from '@rxdi/core';
import * as firebase from 'firebase/app';
import { from, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';

import {
  FacebookProvider,
  GithubProvider,
  GoogleProvider,
  ProvidersType,
  SetProviderType,
  TwitterProvider,
} from './social.injections';

@Injectable({ init: true })
export class SocialService {
  private _isLoggedIn: Subject<boolean> = new Subject();
  constructor() {
    firebase.auth().onAuthStateChanged((user) => {
      this._isLoggedIn.next(!!user);
    });
    /* Get user token based on URI parameteres /?token=mytoken */
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    if (token) {
      this.checkLoggedIn()
        .pipe(
          switchMap((isLoggedIn) =>
            from(firebase.auth().signInWithCustomToken(token)).pipe(
              map(() => isLoggedIn),
              catchError(() => of(isLoggedIn))
            )
          )
        )
        .subscribe(() => {
          window.history.pushState({}, document.title, window.location.pathname);
        });
    }
  }

  isLoggedIn = this._isLoggedIn.asObservable();

  userloggedIn() {
    this._isLoggedIn.next(true);
  }

  getUser(): Observable<firebase.User> {
    return new Observable((observer) => {
      const sub = firebase.auth().onAuthStateChanged((user) => {
        observer.next(user);
        observer.complete();
      });
      return sub;
    });
  }

  getUserRef() {
    return of(firebase.auth().currentUser);
  }

  signInWithGithub(fn: SetProviderType<GithubProvider> = (p) => p) {
    return from(firebase.auth().signInWithPopup(fn(new firebase.auth.GithubAuthProvider())));
  }

  signInWithFacebook(fn: SetProviderType<FacebookProvider> = (p) => p) {
    return from(firebase.auth().signInWithPopup(fn(new firebase.auth.FacebookAuthProvider())));
  }

  sendSignInLinkToEmail(email: string, returnUrl: string) {
    if (!returnUrl) {
      return throwError('missing-return-url');
    }
    return from(
      firebase.auth().sendSignInLinkToEmail(email, {
        handleCodeInApp: true,
        url: returnUrl,
      })
    );
  }

  signInWithGoogle(fn: SetProviderType<GoogleProvider> = (p) => p) {
    return from(firebase.auth().signInWithPopup(fn(new firebase.auth.GoogleAuthProvider())));
  }

  signInWithTwitter(fn: SetProviderType<TwitterProvider> = (p) => p) {
    return from(firebase.auth().signInWithPopup(fn(new firebase.auth.TwitterAuthProvider())));
  }

  signInWithEmailAndPassword(email: string, password: string) {
    return from(firebase.auth().signInWithEmailAndPassword(email, password));
  }
  createUserWithEmailAndPassword(email: string, password: string) {
    return from(firebase.auth().createUserWithEmailAndPassword(email, password));
  }

  signOut() {
    return from(firebase.auth().signOut()).pipe(
      tap(() => {
        this._isLoggedIn.next(false);
        localStorage.clear();
      })
    );
  }

  checkLoggedIn() {
    return new Observable<boolean>((o) => {
      const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
        o.next(!!user);
        o.complete();
        this._isLoggedIn.next(!!user);
        unsubscribe();
      });
      return () => unsubscribe();
    });
  }

  resetPassword(email: string) {
    return from(firebase.auth().sendPasswordResetEmail(email));
  }

  linkAccount(type: ProvidersType) {
    return this.getUserRef().pipe(
      filter((userRef) => !!userRef),
      switchMap((userRef) => userRef.linkWithPopup(this.getProvider(type)))
    );
  }

  getProvider(type: ProvidersType) {
    if (type === 'facebook') {
      return new firebase.auth.FacebookAuthProvider();
    }

    if (type === 'github') {
      return new firebase.auth.GithubAuthProvider();
    }

    if (type === 'google') {
      return new firebase.auth.GoogleAuthProvider();
    }

    if (type === 'twitter') {
      return new firebase.auth.TwitterAuthProvider();
    }
  }

  updateProfile(displayName: string, photoURL: string) {
    return this.getUserRef().pipe(
      switchMap((res) =>
        res.updateProfile({
          displayName,
          photoURL,
        })
      )
    );
  }
}
